import {
  IconMenu2,
  IconBell,
  IconCaretDownFilled,
  IconX,
  IconChevronRight,
} from "@tabler/icons-react";
import React, { useEffect, useState, useRef } from "react";
import Notifications from "../../common/Notification";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getstudentNotification,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "../../../services/user.services";
import LogoutButton from "./LogoutButton";

const Header = ({ isOpen, setIsOpen }) => {
  // State management
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Redux
  const user_id = useSelector((state) => state.user.user_id);
  const user = useSelector((state) => state.user.userDetails);

  // Navigation and location
  const navigate = useNavigate();
  const location = useLocation();

  // Dynamic title based on current path
  const getDynamicTitle = () => {
    const path = location.pathname;

    // Document Upload paths
    const documentPaths = {
      "/student/documents_upload/cv_upload": "Documents Upload > CV Upload",
      "/student/documents_upload/sop": "Documents Upload > SOP",
      "/student/documents_upload/lor": "Documents Upload > LOR",
      "/student/documents_upload/university_specific_sop":
        "Documents Upload > University Specific SOP",
      "/student/documents_upload/ug_only": "Documents Upload > UG Only",
      "/student/documents_upload/final_documents_upload":
        "Documents Upload > Final Documents Upload",
      "/student/documents_upload/shortlisting_of_universities":
        "Documents Upload > Shortlisting of Universities",
    };

    // Check if path is in document paths
    if (documentPaths[path]) {
      return documentPaths[path];
    }

    if (path.includes("/student/next_step_after_admit")) {
      return "Next Step After Admit";
    }

    // Check for other specific paths
    if (path.includes("/student/university_application_process")) {
      return "University Application Process";
    }

    if (path.includes("/student/visa_process")) {
      return "Visa Process";
    }

    // Default title for other paths
    return "Status";
  };

  // Refs for click outside detection
  const notificationRef = useRef(null);
  const bellIconRef = useRef(null);
  const dropdownRef = useRef(null);
  const profileRef = useRef(null);
  const prevNotificationCount = useRef(0);

  // Window resize handler
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Toggle handlers
  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    if (showDropdown) setShowDropdown(false);
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
    if (showNotifications) setShowNotifications(false);
  };

  const handleToggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const handleEditProfile = () => {
    navigate("/student/edit-profile");
    setShowDropdown(false);
  };

  // Get notifications
  const getNotifications = async () => {
    try {
      const res = await getstudentNotification(user_id);
      const newData = res?.data?.notifications;

      // Check if there are new notifications

      prevNotificationCount.current = newData ? newData.length : 0;
      setNotifications(newData || []);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  // Handle notifications
  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead(user_id);
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, mark_as_read: true }))
      );
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, mark_as_read: true } : n
        )
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Click outside detection
  useEffect(() => {
    function handleClickOutside(event) {
      // Close notification if click is outside
      if (
        showNotifications &&
        notificationRef.current &&
        !notificationRef.current.contains(event.target) &&
        bellIconRef.current &&
        !bellIconRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }

      // Close dropdown if click is outside
      if (
        showDropdown &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        profileRef.current &&
        !profileRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotifications, showDropdown]);

  // Fetch notifications
  useEffect(() => {
    let intervalId;

    if (user_id) {
      getNotifications(); // Initial fetch

      intervalId = setInterval(() => {
        getNotifications();
      }, 30000); // Every 30 seconds
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [user_id]);

  // Helper functions
  const getUnreadCount = () => {
    return notifications?.filter((n) => !n.mark_as_read).length || 0;
  };

  return (
    <div className="relative z-50">
      {/* Header Container */}
      <div className="h-16 md:h-20 bg-white flex items-center justify-between px-3 md:px-6 lg:px-8 shadow-sm transition-all duration-300">
        {/* Left Side - Hamburger Menu and Logo */}
        <div className="flex items-center space-x-3">
          {/* Hamburger Menu */}
          <div
            className="cursor-pointer hover:bg-gray-100 p-2 rounded-lg transition-colors"
            onClick={handleToggleSidebar}
          >
            <IconMenu2
              stroke={2}
              size={isMobile ? 20 : 24}
              className="text-gray-600"
            />
          </div>

          {/* Logo */}
          <div className="block md:hidden max-w-[90px] md:max-w-[115px]">
            <img src="/logo.png" alt="logo" className="h-8 md:h-10" />
          </div>

          {/* Dynamic Title */}
          <div className="hidden md:flex items-center text-navy">
            <h1 className="text-xl font-bold">{getDynamicTitle()}</h1>
          </div>
        </div>

        {/* Right Side - Action Buttons + Profile */}
        <div className="flex items-center space-x-2 md:space-x-4 lg:space-x-6">
          {/* Action Button - Apply */}
          <button
            className="hidden md:block bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-3 rounded-lg text-base transition-transform duration-300 hover:scale-105"
            onClick={() => navigate("/student/mainForex")}
          >
            Apply for Forex Card or Education Loan,
            <br />
            Accomodation and Insurance
          </button>

          {/* Mobile Action Button */}
          <button
            className="block md:hidden bg-green-500 hover:bg-green-600 text-white font-semibold px-2 py-1.5 rounded-lg text-xs transition-transform"
            onClick={() => navigate("/student/mainForex")}
          >
            Apply for Loan,
            <br />
            Forex Card & More
          </button>

          {/* Notification Bell with Dropdown */}
          <div className="relative">
            <div
              ref={bellIconRef}
              className="relative bg-red-500 hover:bg-red-600 p-1.5 md:p-2 rounded-lg transition-transform duration-300 hover:scale-105 cursor-pointer"
              onClick={toggleNotifications}
            >
              <IconBell
                stroke={2}
                size={isMobile ? 18 : 22}
                className="text-white"
              />
              {getUnreadCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-gray-200 text-red-600 text-xs w-5 h-5 rounded-full flex items-center justify-center font-semibold">
                  {getUnreadCount()}
                </span>
              )}
            </div>

            {/* Notification dropdown */}
            {showNotifications && (
              <div
                ref={notificationRef}
                className="absolute right-0 mt-2"
                style={{
                  transform: "translateX(calc(0% - 100px))", // Center relative to the bell
                  right: "-140px", // Adjust this value as needed
                }}
              >
                <Notifications
                  notifications={notifications}
                  handleMarkAllAsRead={handleMarkAllAsRead}
                  handleMarkAsRead={handleMarkAsRead}
                />
              </div>
            )}
          </div>

          {/* Profile Icon with Dropdown */}
          <div ref={profileRef} className="relative">
            <div className="cursor-pointer" onClick={toggleDropdown}>
              <UserAvatar user={user} />
            </div>

            {/* Profile Dropdown Menu */}
            {showDropdown && (
              <div
                ref={dropdownRef}
                className="absolute top-12 right-0 bg-white border shadow-lg rounded-lg w-60 overflow-hidden z-50"
              >
                <div className="px-4 py-3 border-b bg-gray-50 flex items-center space-x-3">
                  <UserAvatar user={user} />
                  <div>
                    <p className="text-sm font-medium">
                      {user?.username || "User"}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {user?.email || ""}
                    </p>
                  </div>
                </div>

                <button
                  className="block w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-100 border-b transition-colors"
                  onClick={handleEditProfile}
                >
                  <span className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    Edit Profile
                  </span>
                </button>

                {/* <button
                  className="block w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-100 border-b transition-colors"
                  onClick={handleToggleSidebar}
                >
                  <span className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    </svg>
                    Toggle Sidebar
                  </span>
                </button> */}

                <LogoutButton />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;

// User Avatar Component
const UserAvatar = ({ user }) => {
  const [imageError, setImageError] = useState(false);

  const getInitials = (username) => {
    if (!username) return "U";
    const parts = username.trim().split(" ");
    const first = parts[0]?.[0] || "";
    const last = parts[1]?.[0] || "";
    return (first + last).toUpperCase();
  };

  const initials = getInitials(user?.username);

  return (
    <>
      {imageError || !user?.profile_picture_url ? (
        <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-purple-500 text-white rounded-full font-bold text-sm">
          {initials}
        </div>
      ) : (
        <img
          src={user?.profile_picture_url}
          alt="Profile"
          className="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover border-2 border-white shadow-sm"
          onError={() => setImageError(true)}
        />
      )}
    </>
  );
};
